import React, { useState } from "react";
import Button from "@amzn/meridian/esm/components/button";
import Column from "@amzn/meridian/esm/components/column";
import Divider from "@amzn/meridian/esm/components/divider";
import Link from "@amzn/meridian/esm/components/link";
import Row from "@amzn/meridian/esm/components/row";
import Text from "@amzn/meridian/esm/components/text";
import Theme from "@amzn/meridian/esm/components/theme/theme";

import { openUrl } from "../../utils/zoomSdkUtils";
import { PHONE_TOOL_USER_BASE } from "../../constants";
import chevronIcon from "../../images/chevron.svg";
import copyButtonIcon from "../../images/copy_button_icon.svg";
import joinedIcon from "../../images/joined-icon.svg";
import absentIcon from "../../images/absent-icon.svg";
import { THEME_COLOR } from "../../styles/colors";

/**
 * StatusIcon — renders a joined (green circle + white tick) or absent (grey circle + white X).
 */
const StatusIcon = ({ joined }) => (
  <img
    src={joined ? joinedIcon : absentIcon}
    alt={joined ? "Joined" : "Absent"}
  />
);

/**
 * MeetingParticipants — Full meeting participants view.
 *
 * Separate "Internal participants" and "External participants" sections.
 * - Internal table: 2 columns — NAME (joined status + linked name + alias below), ROLE
 * - External table: 2 columns — NAME (joined status + name), EMAIL
 *
 * Props:
 * - attendees: Array of { displayName, alias, email, title, external, joined, adHoc }
 * - onBack: Callback to return to the summary detail view
 */
const MeetingParticipants = ({ attendees, onBack }) => {
  const [copySuccess, setCopySuccess] = useState(false);

  // Sort joined participants before absent (true=1 > false=0, so b-a gives descending order)
  const internals = attendees.filter((a) => !a.external).sort((a, b) => b.joined - a.joined);
  const externals = attendees.filter((a) => a.external).sort((a, b) => b.joined - a.joined);
  const internalJoined = internals.filter((a) => a.joined).length;
  const externalJoined = externals.filter((a) => a.joined).length;
  const hasAdHoc = attendees.some((a) => a.adHoc);

  const handleCopy = async () => {
    const lines = [];
    if (internals.length > 0) {
      lines.push("Internal participants:");
      internals.forEach((a) => {
        const name = a.displayName || a.email || "N/A";
        const alias = a.alias || "N/A";
        const role = a.title || "";
        const status = a.joined ? "Joined" : "Did not attend";
        const adHoc = a.adHoc ? " *" : "";
        lines.push(`  ${name} (${alias}) - ${role} - ${status}${adHoc}`);
      });
    }
    if (externals.length > 0) {
      lines.push("");
      lines.push("External participants:");
      externals.forEach((a) => {
        const name = a.displayName || a.email || "N/A";
        const email = a.email || "N/A";
        const status = a.joined ? "Joined" : "Did not attend";
        const adHoc = a.adHoc ? " *" : "";
        lines.push(`  ${name} - ${email} - ${status}${adHoc}`);
      });
    }
    if (hasAdHoc) {
      lines.push("");
      lines.push("* Not in original meeting invite");
    }

    const text = lines.join("\n");

    const fallbackCopy = (content) => {
      const textarea = document.createElement("textarea");
      textarea.value = content;
      textarea.style.position = "fixed";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    };

    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      fallbackCopy(text);
    }
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 3000);
  };

  return (
    <Column spacing="400" spacingInset="none">
      {/* Back button */}
      <Row spacing="none" spacingInset="none">
        <Button type="link" size="small" onClick={onBack}>
          <Row spacing="300" spacingInset="none" alignmentVertical="center">
            <img src={chevronIcon} alt="" />
            <Theme tokens={{ textColorPrimary: THEME_COLOR.TEAL_GREEN }}>
              <Text type="b200">Back to summary</Text>
            </Theme>
          </Row>
        </Button>
      </Row>

      {/* ===== Internal participants section ===== */}
      {internals.length > 0 && (
        <Column spacing="200" spacingInset="none">
          {/* Section header: title + joined count + copy button */}
          <Row spacing="none" spacingInset="none" alignmentVertical="top" widths={["fill", "fit"]}>
            <Column spacing="200">
              <Text type="h100">Internal participants</Text>
              <Text type="b200" color="secondary">
                {internalJoined}/{internals.length} joined
              </Text>
            </Column>

            {/* Copy button + "copied" confirmation (reserves space, shown on copy) */}
            <Column spacing="200" spacingInset="none" alignmentHorizontal="end">
              <Theme
                tokens={{
                  buttonBorderColorTertiaryDefault: THEME_COLOR.MID_GRAY,
                  buttonBorderColorTertiaryHover: THEME_COLOR.MID_GRAY,
                  buttonBackgroundColorTertiaryHover: THEME_COLOR.MIST_GRAY,
                  buttonBorderColorTertiaryPressed: THEME_COLOR.MID_GRAY,
                  buttonBackgroundColorTertiaryPressed: THEME_COLOR.FROST_GRAY,
                  buttonBorderWidth: "1px",
                }}
              >
                <Button type="tertiary" size="small" onClick={handleCopy} aria-label="Copy participant list">
                  <img src={copyButtonIcon} alt="Copy" />
                </Button>
              </Theme>
              {copySuccess ? (
                <Row spacing="200" spacingInset="none" alignmentVertical="center">
                  <img src={joinedIcon} alt="" />
                  <Text type="b100" color="success">Participant list copied</Text>
                </Row>
              ) : (
                <Row height="20px" spacingInset="none" />
              )}
            </Column>
          </Row>

          {/* Table: column headers + participant rows */}
          <Column spacing="none" spacingInset="none">
            {/* Column headers */}
            <Column spacing="300" spacingInset="none">
              <Row spacing="none" spacingInset="none" widths={["55%", "45%"]}>
                <Theme tokens={{ textB100FontSize: "12px", textB100FontWeight: "700", textColorSecondary: THEME_COLOR.MID_GRAY }}>
                  <Text type="b100" color="secondary">NAME</Text>
                  <Text type="b100" color="secondary">ROLE</Text>
                </Theme>
              </Row>
              <Theme tokens={{ dividerColor: THEME_COLOR.MIST_GRAY }}>
                <Divider spacingBefore="none" spacingAfter="none" />
              </Theme>
            </Column>

            {/* Internal participant rows */}
            {internals.map((attendee, index) => (
              <Column key={`internal-${index}`} spacing="none" spacingInset="none">
                <Row spacing="none" spacingInset="350 none" widths={["55%", "45%"]} alignmentVertical="top">
                  {/* Name column */}
                  <Row spacing="300" spacingInset="none" alignmentVertical="top">
                    <StatusIcon joined={attendee.joined} />
                    <Column spacing="none">
                      {attendee.alias ? (
                        <Row spacing="100" spacingInset="none" alignmentVertical="center">
                          <Link onClick={() => openUrl(`${PHONE_TOOL_USER_BASE}${attendee.alias}`)}>
                            <Theme tokens={{ textColorPrimary: THEME_COLOR.TEAL_GREEN }}>
                              <Text color="primary" type="b200" breakWord>
                                <u>{attendee.displayName || attendee.email || "N/A"}</u>
                              </Text>
                            </Theme>
                          </Link>
                          {attendee.adHoc && <Text type="b100" color="secondary">*</Text>}
                        </Row>
                      ) : (
                        <Row spacing="100" spacingInset="none" alignmentVertical="center">
                          <Text type="b200" breakWord>{attendee.displayName || attendee.email || "N/A"}</Text>
                          {attendee.adHoc && <Text type="b100" color="secondary">*</Text>}
                        </Row>
                      )}
                      {attendee.alias && <Text type="b200">{attendee.alias}</Text>}
                    </Column>
                  </Row>

                  {/* Role column */}
                  <Text type="b200" color="secondary" breakWord>
                    {attendee.title || ""}
                  </Text>
                </Row>
                <Theme tokens={{ dividerColor: THEME_COLOR.MIST_GRAY }}>
                  <Divider spacingBefore="none" spacingAfter="none" />
                </Theme>
              </Column>
            ))}
          </Column>
        </Column>
      )}

      {/* ===== External participants section ===== */}
      {externals.length > 0 && (
        <Column spacing="400" spacingInset="500 none none none">
          {/* Section header */}
          <Column spacing="200">
            <Text type="h100">External participants</Text>
            <Text type="b200" color="secondary">
              {externalJoined}/{externals.length} joined
            </Text>
          </Column>

          {/* Table: column headers + participant rows */}
          <Column spacing="none" spacingInset="none">
            {/* Column headers */}
            <Column spacing="300" spacingInset="none">
              <Row spacing="none" spacingInset="none" widths={["50%", "50%"]}>
                <Theme tokens={{ textB100FontSize: "12px", textB100FontWeight: "700", textColorSecondary: THEME_COLOR.MID_GRAY }}>
                  <Text type="b100" color="secondary">NAME</Text>
                  <Text type="b100" color="secondary">EMAIL</Text>
                </Theme>
              </Row>
              <Theme tokens={{ dividerColor: THEME_COLOR.MIST_GRAY }}>
                <Divider spacingBefore="none" spacingAfter="none" />
              </Theme>
            </Column>

            {/* External participant rows */}
            {externals.map((attendee, index) => (
              <Column key={`external-${index}`} spacing="none" spacingInset="none">
                <Row spacing="none" spacingInset="350 none" widths={["50%", "50%"]} alignmentVertical="center">
                  {/* Name column — icon + name */}
                  <Row spacing="300" spacingInset="none" alignmentVertical="center">
                    <StatusIcon joined={attendee.joined} />
                    <Row spacing="100" spacingInset="none" alignmentVertical="center">
                      <Text type="b200" breakWord>{attendee.displayName || attendee.email || "N/A"}</Text>
                      {attendee.adHoc && <Text type="b100" color="secondary">*</Text>}
                    </Row>
                  </Row>

                  {/* Email column */}
                  <Text type="b200" color="secondary" breakWord>
                    {attendee.email || "N/A"}
                  </Text>
                </Row>
                <Theme tokens={{ dividerColor: THEME_COLOR.MIST_GRAY }}>
                  <Divider spacingBefore="none" spacingAfter="none" />
                </Theme>
              </Column>
            ))}
          </Column>
        </Column>
      )}

      {/* Ad-hoc footnote */}
      {hasAdHoc && (
        <Text type="b100" color="secondary">
          * Not in original meeting invite
        </Text>
      )}
    </Column>
  );
};

export default MeetingParticipants;
